import React, { useState } from 'react';

// ─── colour helpers ──────────────────────────────────────────────────────────

const DIR_COLOURS = { North: '#4a90d9', South: '#e86c2f', East: '#f5c842', West: '#8b5cf6' };
const DIR_ICONS   = { North: '↑', South: '↓', East: '→', West: '←' };

const saiGrade = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '–';
    if (n >= 0.85) return 'A';
    if (n >= 0.70) return 'B';
    if (n >= 0.55) return 'C';
    if (n >= 0.40) return 'D';
    return 'E';
};

const gradeColour = { A: '#22c55e', B: '#84cc16', C: '#eab308', D: '#f97316', E: '#ef4444' };

const intensityColour = (val, maxVal) => {
    if (!maxVal) return '#94a3b8';
    const t = Math.min(val / maxVal, 1);
    const r = Math.round(74  + (232 - 74)  * t);
    const g = Math.round(144 + (108 - 144) * t);
    const b = Math.round(217 + (47  - 217) * t);
    return `rgb(${r},${g},${b})`;
};

const fmt = (v, dec = 1) => {
    const n = parseFloat(v);
    return isNaN(n) ? (v ?? '–') : n.toFixed(dec);
};

// ─── DirectionCard ───────────────────────────────────────────────────────────

const DirectionCard = ({ dir, data, maxIntensity }) => {
    const colour = intensityColour(data.intensity, maxIntensity);
    const barPct = maxIntensity ? Math.round((data.intensity / maxIntensity) * 100) : 0;
    return (
        <div style={{
            flex: '1 1 90px', background: '#1e293b', borderRadius: 10, padding: '10px 12px',
            border: `1.5px solid ${colour}`, textAlign: 'center', minWidth: 90
        }}>
            <div style={{ fontSize: 22, color: colour }}>{DIR_ICONS[dir]}</div>
            <div style={{ fontWeight: 700, color: '#f1f5f9', fontSize: 13, marginTop: 2 }}>{dir}</div>
            <div style={{ height: 4, borderRadius: 2, background: '#334155', margin: '6px 0 4px', overflow: 'hidden' }}>
                <div style={{ width: `${barPct}%`, height: '100%', background: colour, borderRadius: 2 }} />
            </div>
            <div style={{ color: '#94a3b8', fontSize: 10 }}>Intensity</div>
            <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 12 }}>
                {fmt(data.intensity, 0)} <span style={{ color: '#94a3b8', fontWeight: 400 }}>kWh/m²</span>
            </div>
            <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 4 }}>Avg rate</div>
            <div style={{ color: '#fbbf24', fontWeight: 600, fontSize: 11 }}>{fmt(data.rate)} W/m²</div>
            <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 4 }}>Peak sun hrs</div>
            <div style={{ color: '#a5f3fc', fontWeight: 600, fontSize: 11 }}>{fmt(data.peak_sun_hours)} h/day</div>
        </div>
    );
};

// ─── OverviewMetrics ─────────────────────────────────────────────────────────

const OverviewMetrics = ({ ov }) => {
    const metrics = [
        { label: 'Avg Radiation Rate',  value: `${fmt(ov.radiation_rate_avg)} W/m²` },
        { label: 'Annual Total',         value: `${fmt(ov.annual_radiation_total, 0)} kWh/m²` },
        { label: 'Sunlight Hours/Day',   value: `${fmt(ov.sunlight_hours_per_day)} h` },
        { label: 'Shadow Duration',      value: `${fmt(ov.shadow_duration_percent)} %` },
        { label: 'Sky View Factor',      value: fmt(ov.sky_view_factor, 2) },
        { label: 'Direct Irradiance',    value: `${fmt(ov.direct_irradiance)} W/m²` },
        { label: 'Diffuse Irradiance',   value: `${fmt(ov.diffuse_irradiance)} W/m²` },
        { label: 'Reflected Irradiance', value: `${fmt(ov.reflected_irradiance)} W/m²` },
        { label: 'Solar Heat Gain',      value: `${fmt(ov.solar_heat_gain)} kWh` },
    ];
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: '6px 10px', marginTop: 10 }}>
            {metrics.map(({ label, value }) => (
                <div key={label} style={{ background: '#1e293b', borderRadius: 8, padding: '7px 10px', borderLeft: '3px solid #f59e0b' }}>
                    <div style={{ color: '#94a3b8', fontSize: 10 }}>{label}</div>
                    <div style={{ color: '#fef3c7', fontWeight: 600, fontSize: 12 }}>{value}</div>
                </div>
            ))}
        </div>
    );
};

// ─── LayerRow ────────────────────────────────────────────────────────────────

const LayerRow = ({ layerName, layerData }) => {
    const [open, setOpen] = useState(false);
    const { overview = {}, directions = {} } = layerData;
    const grade   = saiGrade(overview.solar_access_index);
    const gColour = gradeColour[grade] || '#94a3b8';
    const maxIntensity = Math.max(...Object.values(directions).map(d => d.intensity || 0), 1);

    return (
        <div style={{ marginBottom: 8, border: '1px solid #334155', borderRadius: 10, overflow: 'hidden', background: '#0f172a' }}>
            <div
                role="button"
                tabIndex={0}
                onClick={() => setOpen(o => !o)}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setOpen(o => !o)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 14px', cursor: 'pointer',
                    background: open ? '#1e293b' : 'transparent', userSelect: 'none'
                }}
            >
                <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: gColour + '22', border: `2px solid ${gColour}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 15, color: gColour
                }}>{grade}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {layerName}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 1 }}>
                        {fmt(overview.sunlight_hours_per_day)} h/day sun &nbsp;·&nbsp;
                        {fmt(overview.shadow_duration_percent)}% shadow &nbsp;·&nbsp;
                        SAI {fmt(overview.solar_access_index, 2)}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {['North','South','East','West'].map(d => directions[d]
                        ? <div key={d} style={{ width: 8, height: 8, borderRadius: '50%', background: DIR_COLOURS[d] }} />
                        : null
                    )}
                </div>
                <div style={{ color: '#64748b', fontSize: 16, flexShrink: 0 }}>{open ? '▲' : '▼'}</div>
            </div>

            {open && (
                <div style={{ padding: '12px 14px', borderTop: '1px solid #1e293b' }}>
                    {Object.keys(directions).length > 0 && (
                        <>
                            <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Directional Analysis
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                                {Object.entries(directions).map(([dir, d]) => (
                                    <DirectionCard key={dir} dir={dir} data={d} maxIntensity={maxIntensity} />
                                ))}
                            </div>
                        </>
                    )}
                    <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Surface Metrics
                    </div>
                    <OverviewMetrics ov={overview} />
                    {overview.annotation && (
                        <div style={{ marginTop: 12, background: '#1e3a5f', border: '1px solid #2563eb', borderRadius: 8, padding: '9px 12px', color: '#bfdbfe', fontSize: 12, lineHeight: 1.6 }}>
                            <span style={{ color: '#60a5fa', fontWeight: 700 }}>💡 </span>{overview.annotation}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── stage summary helper ────────────────────────────────────────────────────

const calcStageSummary = (stageData) => {
    const ovs = Object.values(stageData).map(l => l.overview || {});
    const n = ovs.length || 1;
    return {
        avgRate: (ovs.reduce((s, o) => s + (parseFloat(o.radiation_rate_avg) || 0), 0) / n).toFixed(1),
        avgSAI:  (ovs.reduce((s, o) => s + (parseFloat(o.solar_access_index)  || 0), 0) / n).toFixed(2),
    };
};

// ─── LEGACY helper stubs (kept to avoid reference errors if utils is still imported elsewhere)
const getSystemIcon = (systemName) => {
    const normalizedName = systemName.toLowerCase().trim();
    
    // Map system names to appropriate emojis
    const iconMap = {
        'structural': '🏗️',
        'structure': '🏗️',
        'electric': '⚡',
        'electrical': '⚡',
        'electricity': '⚡',
        'hvac': '🌡️',
        'heating': '🌡️',
        'ventilation': '🌡️',
        'cooling': '🌡️',
        'plumbing': '🚰',
        'water': '🚰',
        'piping': '🚰',
        'fire safety': '🚒',
        'fire': '🚒',
        'safety': '🚒',
        'sprinkler': '🚒',
        'lighting': '💡',
        'light': '💡',
        'lights': '💡',
        'illumination': '💡'
    };
    
    // Check for exact match first
    if (iconMap[normalizedName]) {
        return iconMap[normalizedName];
    }
    
    // Check for partial match (if system name contains the keyword)
    for (const [key, icon] of Object.entries(iconMap)) {
        if (normalizedName.includes(key)) {
            return icon;
        }
    }
    
    // Default fallback icon
    return '🏢';
};


// ─── Main Widget ─────────────────────────────────────────────────────────────

const SolarRadiationWidget = ({ data }) => {
    const [activeStage, setActiveStage] = useState(null);

    const responseData = (data && data.response) ? data.response : data;

    if (!responseData || typeof responseData !== 'object') {
        return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 32, fontSize: 14 }}>No solar radiation data available.</div>;
    }

    const stages = Object.keys(responseData);
    if (stages.length === 0) {
        return <div style={{ color: '#94a3b8', textAlign: 'center', padding: 32, fontSize: 14 }}>No solar radiation data available.</div>;
    }

    const currentStage = activeStage || stages[0];
    const stageData    = responseData[currentStage] || {};
    const { avgRate, avgSAI } = calcStageSummary(stageData);
    const grade   = saiGrade(avgSAI);
    const gColour = gradeColour[grade] || '#94a3b8';

    return (
        <div style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#f1f5f9', padding: '0 2px' }}>

            {/* Header */}
            <div style={{
                background: 'linear-gradient(135deg, #78350f 0%, #b45309 50%, #f59e0b 100%)',
                borderRadius: 12, padding: '14px 18px', marginBottom: 14,
                display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap'
            }}>
                <div style={{ fontSize: 36 }}>☀️</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#fef3c7', fontWeight: 800, fontSize: 17 }}>Solar Radiation Analysis</div>
                    <div style={{ color: '#fde68a', fontSize: 12, marginTop: 2 }}>
                        Avg rate: <strong>{avgRate} W/m²</strong> &nbsp;·&nbsp; Solar Access Index: <strong>{avgSAI}</strong>
                    </div>
                </div>
                <div style={{
                    width: 52, height: 52, borderRadius: '50%',
                    background: gColour + '33', border: `3px solid ${gColour}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                }}>
                    <span style={{ fontWeight: 900, fontSize: 22, color: gColour, lineHeight: 1 }}>{grade}</span>
                    <span style={{ color: gColour, fontSize: 8, fontWeight: 700 }}>SAI</span>
                </div>
            </div>

            {/* Stage tabs */}
            {stages.length > 1 && (
                <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                    {stages.map(s => (
                        <button key={s} onClick={() => setActiveStage(s)} style={{
                            padding: '5px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                            fontSize: 12, fontWeight: 600,
                            background: currentStage === s ? '#f59e0b' : '#1e293b',
                            color: currentStage === s ? '#1c1917' : '#94a3b8'
                        }}>{s}</button>
                    ))}
                </div>
            )}

            {/* Legend */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
                {Object.entries(DIR_COLOURS).map(([d, c]) => (
                    <span key={d} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#94a3b8' }}>
                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />
                        {d}
                    </span>
                ))}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>Click a row to expand ▼</span>
            </div>

            {/* Layer rows */}
            {Object.entries(stageData).length === 0
                ? <div style={{ color: '#64748b', textAlign: 'center', padding: 20 }}>No elements found.</div>
                : Object.entries(stageData).map(([layerName, layerData]) => (
                    <LayerRow key={layerName} layerName={layerName} layerData={layerData} />
                ))
            }
        </div>
    );
};

export default SolarRadiationWidget;
